import { AlertCircle, Mic, Shield, ExternalLink } from 'lucide-react';
import { getParentDomain } from '@/utils/iframeConfig';

interface MicrophonePermissionErrorProps {
  errorType: string;
  onRetry?: () => void;
  onClose?: () => void;
}

export function MicrophonePermissionError({
  errorType,
  onRetry,
  onClose,
}: MicrophonePermissionErrorProps) {
  const parentDomain = getParentDomain();

  const renderErrorContent = () => {
    if (errorType === 'microphone_iframe_permission') {
      return (
        <>
          <div className="flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
            <Shield className="w-8 h-8 text-red-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Microphone Access Blocked
          </h3>
          <p className="text-gray-600 mb-4">
            This application requires microphone access, but it's currently blocked by the embedding platform's security settings.
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4 text-left">
            <h4 className="font-semibold text-amber-900 mb-2 flex items-center">
              <AlertCircle className="w-4 h-4 mr-2" />
              For {parentDomain || 'Embedding Platform'} Administrators:
            </h4>
            <p className="text-sm text-amber-800 mb-3">
              To enable microphone access in Tiled.com:
            </p>
            <div className="space-y-4">
              <div className="bg-white rounded border border-amber-300 p-4">
                <h5 className="text-xs font-bold text-amber-900 mb-3">Steps for Tiled.com Map Editor:</h5>
                <ol className="list-decimal list-inside text-xs text-amber-800 space-y-2">
                  <li><strong>Locate the iframe object</strong> in your Tiled map editor</li>
                  <li><strong>Open object properties</strong> (right-click → Properties)</li>
                  <li><strong>Add a custom property</strong> named <code className="bg-amber-100 px-1 rounded">policy</code></li>
                  <li><strong>Set the value to:</strong>
                    <div className="bg-amber-50 p-2 mt-1 rounded font-mono">
                      <code>microphone 'self'; camera 'self'; autoplay 'self'</code>
                    </div>
                  </li>
                </ol>
              </div>

              <div className="bg-gray-50 rounded border border-gray-300 p-3">
                <p className="text-xs text-gray-600 mb-2">Alternative HTML approach (if Tiled exports to HTML):</p>
                <div className="bg-white rounded border border-gray-400 p-2 font-mono text-xs overflow-x-auto">
                  <code>
                    &lt;iframe<br />
                    &nbsp;&nbsp;src="{window.location.href}"<br />
                    &nbsp;&nbsp;<span className="text-green-600 font-bold">allow="microphone *; camera *; autoplay *"</span><br />
                    &nbsp;&nbsp;width="100%"<br />
                    &nbsp;&nbsp;height="100%"<br />
                    &gt;&lt;/iframe&gt;
                  </code>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left">
            <h4 className="font-semibold text-blue-900 mb-2">
              Alternative Solution:
            </h4>
            <p className="text-sm text-blue-800 mb-3">
              Open this application directly in a new tab to bypass iframe restrictions:
            </p>
            <a
              href={window.location.href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Open in New Tab
              <ExternalLink className="w-4 h-4 ml-2" />
            </a>
          </div>
        </>
      );
    }

    if (errorType === 'microphone_denied') {
      return (
        <>
          <div className="flex items-center justify-center w-16 h-16 bg-amber-100 rounded-full mb-4">
            <Mic className="w-8 h-8 text-amber-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Microphone Access Denied
          </h3>
          <p className="text-gray-600 mb-4">
            You need to allow microphone access to use this voice-based practice session.
          </p>
          <div className="bg-gray-50 rounded-lg p-4 text-left">
            <p className="text-sm text-gray-700 mb-2">To enable microphone access:</p>
            <ol className="list-decimal list-inside text-sm text-gray-600 space-y-1">
              <li>Click the lock icon in your browser's address bar</li>
              <li>Find "Microphone" in the permissions list</li>
              <li>Change it from "Block" to "Allow"</li>
              <li>Refresh the page and try again</li>
            </ol>
          </div>
        </>
      );
    }

    return (
      <>
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Connection Error
        </h3>
        <p className="text-gray-600">
          Failed to start practice session. Please try again.
        </p>
      </>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-8 flex flex-col items-center max-w-2xl mx-auto">
        {renderErrorContent()}

        <div className="mt-6 flex gap-3">
          {onClose && (
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              Close
            </button>
          )}
          {onRetry && errorType !== 'microphone_iframe_permission' && (
            <button
              onClick={onRetry}
              className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-600 transition-colors"
            >
              Try Again
            </button>
          )}
        </div>
      </div>
    </div>
  );
}